import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import type { ChartDataPoint, ChartPaginationState } from '../shared/types/rate-shop-types';

interface ChartPaginationProps {
  paginationState: ChartPaginationState;
  chartData: ChartDataPoint[];
  onPageChange: (page: number) => void;
  className?: string;
}

export const ChartPagination = ({
  paginationState,
  chartData,
  onPageChange,
  className = 'flex items-center gap-2 sm:ml-auto',
}: ChartPaginationProps) => {
  const { currentPage, totalPages, daysPerPage } = paginationState;

  // Calculate actual date range from chart data
  const getDateRange = (): string => {
    if (!chartData || chartData.length === 0) {
      return 'No data';
    }

    const startIndex = (currentPage - 1) * daysPerPage;
    const endIndex = Math.min(currentPage * daysPerPage - 1, chartData.length - 1);

    const startDate = chartData[startIndex]?.date;
    const endDate = chartData[endIndex]?.date;

    if (!startDate || !endDate) {
      return 'No data';
    }

    const formattedStart = dayjs(startDate).format('M/D');
    const formattedEnd = dayjs(endDate).format('M/D');

    return startDate === endDate ? formattedStart : `${formattedStart} to ${formattedEnd}`;
  };

  return (
    <div className={className}>
      <Button
        variant='outline'
        size='sm'
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        aria-label='Go to previous page'
      >
        <ChevronLeftIcon className='h-4 w-4' />
        Previous
      </Button>

      <span className='text-sm text-muted-foreground px-2'>{getDateRange()}</span>

      <Button
        variant='outline'
        size='sm'
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        aria-label='Go to next page'
      >
        Next
        <ChevronRightIcon className='h-4 w-4' />
      </Button>
    </div>
  );
};
